<template>
  <el-dialog :visible.sync="localVisible" :modal="false" :append-to-body="true" :close-on-click-modal="false"
    width="1030px" top="150px" title="图片预览" @opened="onDialogOpened" @closed="onDialogClosed">
    <div class="imgbox between">
      <div class="image-container" ref="imageContainer">
        <!-- 图片 -->
        <img ref="zoomableImage" :src="imageUrl" alt="Zoomable Image" class="zoomable-image" :style="imageStyle"
          @mousedown="startDrag" @wheel.prevent="handleWheel" @load="onImageLoad" />

        <!-- 标记框 -->
        <div v-for="(marker, index) in markers" :key="index" class="marker-box" style="pointer-events: none;"
          :style="getMarkerBoxStyle(marker)" @click.stop="onMarkerClick(marker)">
          <img class="finger-img" src="../../../static/icon_fingerprint.png" alt="">
          <!-- <span class="marker-label">{{ marker.label }}</span> -->
        </div>
      </div>
      <div class="smallimg">
        <el-image v-for="v, k in markers" class="p" fit="cover" :src="v.imgurl" :preview-src-list="[v.imgurl]">
        </el-image>
      </div>
    </div>

  </el-dialog>
</template>

<script>
export default {
  name: 'ZoomableImageWithMarkers',
  props: {
    visible: { type: Boolean, required: true },
    imageUrl: { type: String, required: true },
    initialMarkers: { type: Array, required: true },
    focusMarker: { // 外部传入，指定打开时聚焦的标记点
      type: Object,
      default: null
    }
  },
  data() {
    return {
      localVisible: this.visible,
      scale: 1,
      offsetX: 0,
      offsetY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,
      startOffsetX: 0,
      startOffsetY: 0,
      containerWidth: 0,
      containerHeight: 0,
      imageWidth: 0,
      imageHeight: 0,
      markers: [],
      resizeObserver: null
    }
  },
  watch: {
    visible(newVal) {
      this.localVisible = newVal;
      if (newVal && this.focusMarker) {
        this.$nextTick(() => {
          // 图片已加载，直接聚焦
          if (this.imageWidth > 0 && this.imageHeight > 0) {
            this.focusOnMarker(this.focusMarker);
          }
          // 否则等 onImageLoad 处理
        });
      }
    },
    localVisible(val) {
      if (!val) {
        this.$emit('update:visible', false);
      }
    },
    initialMarkers: {
      handler(val) {
        this.markers = val.map(marker => ({
          ...marker,
          originalWidth: marker.width,
          originalHeight: marker.height
        }));
      },
      immediate: true,
      deep: true
    },
    focusMarker(newVal) {
      this.$nextTick(() => {
        if (this.localVisible && newVal) {
          this.focusOnMarker(newVal);
        }
      })
    }
  },
  computed: {
    imageStyle() {
      return {
        transform: `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`,
        transformOrigin: '0 0',
        cursor: this.isDragging ? 'grabbing' : 'grab',
        // transition: this.isDragging ? 'none' : 'transform 0.1s ease'
      }
    }
  },
  methods: {
    onImageLoad(e) {
      this.imageWidth = e.target.naturalWidth;
      this.imageHeight = e.target.naturalHeight;

      this.$nextTick(() => {
        this.updateContainerSize();
        if (this.localVisible && this.focusMarker) {
          this.focusOnMarker(this.focusMarker); // ✅ 有聚焦需求，执行聚焦
        } else {
          this.centerImage(); // ✅ 否则才居中
        }
      });
    },

    updateContainerSize() {
      const container = this.$refs.imageContainer;
      if (container) {
        this.containerWidth = container.clientWidth || container.offsetWidth;
        this.containerHeight = container.clientHeight || container.offsetHeight;
      }
    },

    centerImage() {
      this.updateContainerSize();
      if (this.containerWidth <= 0 || this.containerHeight <= 0) return;

      const scaledImgWidth = this.imageWidth * this.scale;
      const scaledImgHeight = this.imageHeight * this.scale;

      this.offsetX = (this.containerWidth - scaledImgWidth) / 2;
      this.offsetY = (this.containerHeight - scaledImgHeight) / 2;
    },

    onDialogOpened() {
      this.$nextTick(() => {
        this.updateContainerSize();

        // 如果图片已经加载，立即处理
        if (this.imageWidth > 0 && this.imageHeight > 0) {
          if (this.focusMarker) {
            this.focusOnMarker(this.focusMarker);
          } else {
            this.centerImage();
          }
        }

        // 监听容器大小变化
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        this.resizeObserver = new ResizeObserver(() => {
          this.updateContainerSize();
          if (this.localVisible) {
            if (this.focusMarker) {
              this.focusOnMarker(this.focusMarker);
            } else {
              this.centerImage();
            }
          }
        });
        this.resizeObserver.observe(this.$refs.imageContainer);

        // 绑定拖拽事件
        document.addEventListener('mousemove', this.drag);
        document.addEventListener('mouseup', this.endDrag);
      });
    },

    onDialogClosed() {
      this.resetTransform();
      document.removeEventListener('mousemove', this.drag);
      document.removeEventListener('mouseup', this.endDrag);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },

    resetTransform() {
      this.scale = 1;
      this.offsetX = 0;
      this.offsetY = 0;
    },

    handleWheel(e) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = this.scale * delta;

      if (newScale < 0.5 || newScale > 5) return;

      const rect = this.$refs.imageContainer.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // 计算鼠标位置在当前缩放下的图像坐标
      const preXRatio = (mouseX - this.offsetX) / this.scale;
      const preYRatio = (mouseY - this.offsetY) / this.scale;

      // 更新缩放
      this.scale = newScale;

      // 重新计算偏移，使缩放中心保持在鼠标位置
      this.offsetX = mouseX - preXRatio * this.scale;
      this.offsetY = mouseY - preYRatio * this.scale;
    },

    startDrag(e) {
      if (e.button !== 0) return;
      this.isDragging = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startOffsetX = this.offsetX;
      this.startOffsetY = this.offsetY;
      e.preventDefault();
    },

    drag(e) {
      if (!this.isDragging) return;
      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      this.offsetX = this.startOffsetX + dx;
      this.offsetY = this.startOffsetY + dy;
    },

    endDrag() {
      this.isDragging = false;
    },

    getMarkerBoxStyle(marker) {
      // 标记框的实际位置 = 图片偏移 + 缩放后标记点坐标
      const left = this.offsetX + marker.x * this.scale;
      const top = this.offsetY + marker.y * this.scale;

      // 标记框的实际大小 = 原始大小 * 缩放比例
      const width = marker.originalWidth * this.scale;
      const height = marker.originalHeight * this.scale;

      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        position: 'absolute',
        zIndex: 10,
        pointerEvents: 'none',
        // border: '2px solid red',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }
    },

    focusOnMarker(marker) {
      const targetScale = 2; // 聚焦放大倍数

      // 获取容器尺寸和中心点
      const rect = this.$refs.imageContainer.getBoundingClientRect();
      const containerCenterX = rect.width / 2;
      const containerCenterY = rect.height / 2;

      // 标记点在原始图片中的坐标（像素）
      const markerX = marker.x;
      const markerY = marker.y;

      // 计算缩放后，标记点在容器中的目标位置 = 容器中心
      this.offsetX = containerCenterX - markerX * targetScale;
      this.offsetY = containerCenterY - markerY * targetScale;
      this.scale = targetScale;

      // 强制更新标记框样式
      this.$forceUpdate();

      console.log(`聚焦标记点 [${marker.label}] (${markerX}, ${markerY})，缩放: ${targetScale}`);
      console.log(`新偏移量: offsetX=${this.offsetX}, offsetY=${this.offsetY}`);
    },

    onMarkerClick(marker) {
      // this.$alert(marker.info || marker.label, `标记: ${marker.label}`, {
      //   confirmButtonText: '确定'
      // });
    }
  }
}
</script>

<style scoped lang="scss">
.image-container {
  width: 800px;
  height: 60vh;
  overflow: hidden;
  position: relative;
  background: #333;
  border: 1px solid #ddd;
}

.zoomable-image {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
  user-select: none;
  max-width: none;
  max-height: none;
  display: block;
}

.marker-box {
  box-sizing: border-box;
  position: absolute;
  z-index: 10;
  pointer-events: none;
  // background: url("../../../static/yspxq_image_crop.png") no-repeat center 100%/100%;
  // transition: transform 0.1s ease;
  display: flex;
  justify-content: center;
  align-content: center;
  // background-color: #51bc9e6e;
  // border: 2px solid #51BC9E;
  background-color: #5195bc7a;
  border: 2px solid #5183bc;

  .finger-img {
    width: 50%;
    height: 50%;
  }
}

.marker-label {
  position: absolute;
  font-size: 12px;
  color: #333;
  background: white;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

::v-deep .el-dialog__body {
  padding: 0;
}

.imgbox {
  display: flex;
  background-color: #eee;
}

.smallimg {
  padding: 15px;

  .p {
    width: 200px;
    height: 200px;
    display: block;
    margin-top: 15px;
    // border-radius: 9px;

    &:first-child {
      margin-top: 0;
    }
  }
}
</style>